import {AxiosError} from 'axios';
import {useQueryClient, UseQueryOptions} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useQuery} from '@eksab/hooks';

import {Position, Player, TeamPlayer} from '../../types';
import {getIndexFromPosition, useLineupStore} from '../useLineupStore';

const getQueryParams = (params: Params) =>
  Object.keys(params).reduce(
    (prev, curr) => (!!params[curr as keyof Params] ? {...prev, [curr]: params[curr as keyof Params]} : prev),
    {},
  );

const queryKeyName = 'team-players';
const queryKey = (params: Params) => [queryKeyName, params];
const queryFn = async (params: Params) => {
  const parsedParams = getQueryParams(params);
  const res = await api.get('v2/players', {
    params: parsedParams,
  });
  return res.data.data;
};

type Params = {
  competition: string;
  team?: string;
  position: Position;
  price_from?: number;
  price_to?: number;
  q?: string;
};

export function useSetQueryPlayerUIState(competitionId: string) {
  const queryClient = useQueryClient();

  const setUIState = (team: string, position: Position, playerId: number, action: 'add' | 'remove') =>
    queryClient.setQueriesData<Player[]>(
      {
        predicate: (query) => {
          if (query.queryKey[0] !== queryKeyName) return false;
          const queryKeyParams = query.queryKey[1] as Params;

          return (
            queryKeyParams.competition === competitionId &&
            queryKeyParams.team === team &&
            queryKeyParams.position === position
          );
        },
      },
      (players) =>
        players
          ? players.map((player) =>
              player.id === playerId
                ? {
                    ...player,
                    added: action === 'add',
                    isSubstitute: action === 'add' && useLineupStore.getState().indicatorPosition.substitute,
                  }
                : player,
            )
          : [],
    );

  return setUIState;
}

export function addUIState(data: TeamPlayer[]) {
  const currentState = useLineupStore.getState();
  const currentPosition = currentState.indicatorPosition.position;
  const currentPositionIndex = getIndexFromPosition(currentPosition);
  const currentPositionSelectedPlayers = [
    currentState.substitutes[currentPositionIndex],
    ...currentState.players[currentPositionIndex],
  ];

  return data.map((player) => ({
    ...player,
    added: !!currentPositionSelectedPlayers.find((p) => p?.id === player.id),
    isSubstitute: player.id === currentPositionSelectedPlayers[0]?.id,
  }));
}

export function usePlayersQuery(params: Params, options?: UseQueryOptions<Player[], AxiosError>) {
  return useQuery<TeamPlayer[], AxiosError, (TeamPlayer & {isSubstitute: boolean; added: boolean})[]>(
    queryKey(params),
    () => queryFn(params),
    {
      ...options,
      enabled: !!params.competition,
      select: addUIState,
      staleTime: 5 * 60 * 1000,
    },
  );
}

usePlayersQuery.queryKey = queryKey;
usePlayersQuery.queryFn = queryFn;
